import {
  RaidPlanAssignmentEntity,
  RaidPlanEncounterAssignmentAbility,
} from 'app/frontend/types/custom/RaidPlan'
import { VisualShape } from 'app/frontend/types/custom/VisualPlan'
import CharacterRole from 'app/frontend/types/custom/CharacterRole'
import { RaidstratsPlayer } from '../../TeamPlans/components/RaidstratsContainer'
import {
  normalizeAssignmentBox,
  normalizeAssignmentCast,
} from '../../TeamPlans/components/assignmentTimeline'
import { buildVisualEntitySlots } from '../../TeamPlans/components/visual_plans/shapes/entityAssignmentMatching'
import {
  EncounterAssignmentEntityOverride,
  visualEntityOverridesAfterSelect,
  visualEntitySlotKey,
} from './eventActivityPlanOverrides'
import { ResolvedAssignmentEntity } from './eventActivityTimelineResolver'

export type EventPlanOverrideSelection = {
  abilityKey: string
  castIds: string[]
  boxId: string
  entityId: string
  override: EncounterAssignmentEntityOverride
}

const participantRoleToCharacterRole: Record<string, CharacterRole> = {
  tank: 'tank',
  healer: 'heal',
  melee: 'melee',
  ranged: 'ranged',
  dps: 'melee',
  anyone: 'melee',
  non_tank: 'melee',
  anyone_ranged: 'ranged',
  anyone_melee: 'melee',
}

export type AbilityVisualFillEntityRef = {
  scopeId: string
  boxId: string
  entityId: string
  castIds: string[]
  characterId?: number
  isOverridden: boolean
}

type BuildAbilityVisualFillByScopeInput = {
  scopes: { id: string; castIndexes: number[] }[]
  assignmentGroups: number
  abilityKey: string
  ability: RaidPlanEncounterAssignmentAbility
  resolvedByScopeBoxKey: Record<string, ResolvedAssignmentEntity[]>
}

export const buildAbilityVisualFillByScope = ({
  scopes,
  assignmentGroups,
  abilityKey,
  ability,
  resolvedByScopeBoxKey,
}: BuildAbilityVisualFillByScopeInput) => {
  const entitiesByScopeId: Record<string, RaidPlanAssignmentEntity[]> = {}
  const refsByScopeId: Record<string, AbilityVisualFillEntityRef[]> = {}
  const resolvedParticipantPlayers: RaidstratsPlayer[] = []

  scopes.forEach((scope) => {
    const castIds = scope.castIndexes
      .map(
        (castIndex) =>
          normalizeAssignmentCast(ability.casts?.[castIndex], assignmentGroups)
            .id,
      )
      .filter((castId): castId is string => Boolean(castId))
    const flatEntities: RaidPlanAssignmentEntity[] = []
    const refs: AbilityVisualFillEntityRef[] = []

    for (let boxIndex = 0; boxIndex < assignmentGroups; boxIndex += 1) {
      const key = `${abilityKey}:${scope.id}:${boxIndex}`
      const resolvedEntities = resolvedByScopeBoxKey[key] ?? []
      const box = normalizeAssignmentBox(
        normalizeAssignmentCast(
          ability.casts?.[scope.castIndexes[0]],
          assignmentGroups,
        ).boxes[boxIndex],
      )
      resolvedEntities.forEach((entity, entityIndex) => {
        const sourceEntity = box.entities[entityIndex] ?? entity.sourceEntity
        if (entity.kind === 'participant') {
          flatEntities.push({
            id: sourceEntity.id,
            kind: 'member',
            character_id: entity.participant.characterId,
            spec_id: entity.participant.specId,
            role:
              participantRoleToCharacterRole[entity.participant.role] ??
              'melee',
            label: entity.participant.name,
          })
          resolvedParticipantPlayers.push({
            name: entity.participant.name,
            className: entity.participant.characterClass,
          })
        } else {
          flatEntities.push(entity.sourceEntity)
        }
        refs.push({
          scopeId: scope.id,
          boxId: box.id ?? '',
          entityId: sourceEntity.id ?? '',
          castIds,
          characterId:
            entity.kind === 'participant'
              ? entity.participant.characterId
              : undefined,
          isOverridden: entity.isOverridden === true,
        })
      })
    }

    entitiesByScopeId[scope.id] = flatEntities
    refsByScopeId[scope.id] = refs
  })

  return { entitiesByScopeId, refsByScopeId, resolvedParticipantPlayers }
}

export const abilityVisualOverrideSelectionsAfterSelect = ({
  refs,
  clickedIndex,
  override,
  abilityKey,
}: {
  refs: AbilityVisualFillEntityRef[]
  clickedIndex: number
  override: EncounterAssignmentEntityOverride
  abilityKey: string
}): EventPlanOverrideSelection[] => {
  const clicked = refs[clickedIndex]
  if (!clicked?.entityId || !clicked.boxId) return []

  const nextOverrides = visualEntityOverridesAfterSelect({
    visualEntityOverrides: {},
    slotAssignments: refs.map((ref) => ({
      slotKey: ref.entityId,
      characterId: ref.characterId,
    })),
    clickedSlotKey: clicked.entityId,
    override,
  })

  return Object.entries(nextOverrides).flatMap(([entityId, slotOverride]) => {
    if (slotOverride.kind === 'empty') return []
    const ref = refs.find((entry) => entry.entityId === entityId)
    if (!ref?.entityId || !ref.boxId) return []
    return [
      {
        abilityKey,
        castIds: ref.castIds,
        boxId: ref.boxId,
        entityId: ref.entityId,
        override: slotOverride,
      },
    ]
  })
}

type AbilityVisualFillScreen = {
  key: string
  scopeId?: string
  screen: { id: string; shapes: VisualShape[] }
  entityAssignments: Map<number, { index: number }>
}

export const abilityVisualOverrideSelectionsFromSlot = ({
  screens,
  refsByScopeId,
  slotKey,
  override,
  abilityKey,
}: {
  screens: AbilityVisualFillScreen[]
  refsByScopeId: Record<string, AbilityVisualFillEntityRef[]>
  slotKey: string
  override: EncounterAssignmentEntityOverride
  abilityKey: string
}): EventPlanOverrideSelection[] => {
  const screenEntry = screens.find((entry) =>
    buildVisualEntitySlots(entry.screen.shapes).some(
      (slot) =>
        visualEntitySlotKey(entry.screen.id, slot.shapeId, slot.localIndex) ===
        slotKey,
    ),
  )
  if (!screenEntry) return []

  const slot = buildVisualEntitySlots(screenEntry.screen.shapes).find(
    (entry) =>
      visualEntitySlotKey(
        screenEntry.screen.id,
        entry.shapeId,
        entry.localIndex,
      ) === slotKey,
  )
  if (!slot) return []

  const badge = screenEntry.entityAssignments.get(slot.number)
  if (!badge) return []

  const scopeId = screenEntry.scopeId ?? screenEntry.key.split(':')[0]
  const refs = refsByScopeId[scopeId] ?? []
  return abilityVisualOverrideSelectionsAfterSelect({
    refs,
    clickedIndex: badge.index,
    override,
    abilityKey,
  })
}
